import React from 'react';
import { mergeStyles, StylesConfig } from 'react-select';

import SelectComponent from '../FormInputs/Select/Select';
import Language from '../enums/Language';
import LanguageOptions from '../enums/LanguageOptions';
import NlIcon from '../images/nl_flag.png';
import EnIcon from '../images/en_flag.png';
import DeIcon from '../images/de_flag.png';
import AppStorage from '../Storage/Storage';
import { CSSObjectWithLabel } from 'react-select/dist/declarations/src/types';
import {
  reInitReadSpeaker,
  changeLanguageReadSpeaker,
} from 'ReadSpeakerReader/ReadSpeakerProvider';

interface LanguageSelectorProps {
  value: Language;
  changeLangHandler: (event: LanguageOptions) => void;
  isSmallScreen?: boolean;
}

const LangOptions = [
  {
    value: Language.Dutch,
    label: 'Dutch',
    icon: NlIcon,
  },
  {
    value: Language.English,
    label: 'English',
    icon: EnIcon,
  },
  {
    value: Language.German,
    label: 'German',
    icon: DeIcon,
  },
];

interface LanguageSelectorState {
  languageOptions: Array<LanguageOptions>;
}

class LanguageSelector extends React.PureComponent<
  LanguageSelectorProps,
  LanguageSelectorState
> {
  private readonly storage: AppStorage;

  largeScreenSelectStyles: StylesConfig = {
    control: (provided: CSSObjectWithLabel) => ({
      ...provided,
      margin: '0',
      cursor: 'pointer',
      border: 'none',
      '&:hover': {
        border: 'none',
        boxShadow: 'none',
      },
    }),
    indicatorsContainer: (provided: CSSObjectWithLabel) => ({
      ...provided,
      marginTop: '2px',
    }),
    dropdownIndicator: (provided: CSSObjectWithLabel) => ({
      ...provided,
      color: 'var(--grey) !important',
      svg: {
        display: 'block',
      },
      ':after': {
        display: 'none',
      },
    }),
  };

  smallScreenSelectStyles: StylesConfig = {
    container: (provided: CSSObjectWithLabel) => ({
      ...provided,
      height: '100%',
    }),
    control: (provided: CSSObjectWithLabel) => ({
      ...provided,
      height: '100%',
    }),
    indicatorsContainer: (provided: CSSObjectWithLabel) => ({
      ...provided,
      marginTop: '0',
      top: '0',
    }),
    singleValue: (provided: CSSObjectWithLabel) => ({
      ...provided,
      top: '50%',
    }),
    valueContainer: (provided: CSSObjectWithLabel) => ({
      ...provided,
      height: '100%',
    }),
  };

  constructor(props: LanguageSelectorProps) {
    super(props);

    const supportedLocales = localStorage.getItem('locales')?.split(',') || [];
    const languageOptions = LangOptions.filter((lang) => {
      if (supportedLocales) {
        return supportedLocales.includes(lang.value.toLowerCase());
      }
      return true;
    });

    this.state = {
      languageOptions,
    };
    this.storage = AppStorage.getInstance();
    if (languageOptions.length === 1) {
      this.storage.setLocale(languageOptions[0].value);
    }
  }

  setLangToLocalStorage = (lang: Language): void =>
    localStorage.setItem('tafNonAuthLang', lang);

  changeHandler = (event: LanguageOptions): void => {
    const { changeLangHandler } = this.props;
    const lang: Language = event.value;
    this.setLangToLocalStorage(lang);

    if (changeLangHandler) {
      changeLangHandler(event);
    }
    reInitReadSpeaker();
    changeLanguageReadSpeaker(lang);
  };

  render() {
    const { value, isSmallScreen } = this.props;

    const { languageOptions } = this.state;

    if (languageOptions.length === 1) {
      return null;
    }

    return (
      <SelectComponent
        id="langSelection"
        changeHandler={this.changeHandler}
        value={value}
        options={languageOptions}
        isIconOption={true}
        customStyles={
          isSmallScreen
            ? mergeStyles(
                this.largeScreenSelectStyles,
                this.smallScreenSelectStyles,
              )
            : this.largeScreenSelectStyles
        }
      />
    );
  }
}

export default LanguageSelector;
